import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAsset, getAssetHistory, allocateAsset, sendDeclarationEmail } from '../../../../models/assetsModel';
import { getUsers } from '../../../../models/userModel';
import { useAuth } from '../../../../context/AuthContext';
import { toast } from 'react-toastify';
import Modal from '../../../../components/common/Modal';

function ViewHardwareModal({ isOpen, onClose, asset, onSuccess }) {
  const [assetData, setAssetData] = useState(null);
  const [assetHistory, setAssetHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAllocationForm, setShowAllocationForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const { can } = useAuth();
  const navigate = useNavigate();

  const [allocationData, setAllocationData] = useState({
    asset_id: '',
    allocate_staff_id: '',
    allocate_type: '',
    carry_bag: false,
    other_hardware: false,
    keyboard: false,
    mouse: false,
    windows_version: false,
    office_version: false,
    declaration: false
  });

  useEffect(() => {
    if (isOpen && asset) {
      fetchAssetData();
      fetchAssetHistory();
      fetchUsers();
      // Initialize allocation data with current asset info
      setAllocationData(prev => ({ 
        ...prev, 
        asset_id: asset.asset_id,
        allocate_staff_id: asset.user?.user_id || asset.asset_staff_id || asset.allocate_staff_id || '',
        allocate_type: asset.allocate_type || ''
      }));
    }
  }, [isOpen, asset]);

  const fetchAssetData = async () => {
    try {
      setLoading(true);
      const data = await getAsset(asset.asset_id);
      setAssetData(data || asset);
    } catch (error) {
      console.error('Error fetching asset data:', error);
      setAssetData(asset);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetHistory = async () => {
    try {
      // For now, we'll use the history data that might be included in the asset data
      // In the future, this could be a separate API call
      const data = await getAsset(asset.asset_id);
      setAssetHistory(data?.history || []);
    } catch (error) {
      console.error('Error fetching asset history:', error);
      setAssetHistory([]);
    }
  };

  const fetchUsers = async () => {
    try {
      let allUsers = [];
      let currentPage = 1;
      let totalPages = 1;
      
      // Fetch all pages of users
      do {
        const params = new URLSearchParams();
        params.append('page', currentPage.toString());
        params.append('pageSize', '100');
        const response = await getUsers(params);
        
        if (response && response.data && response.data.rows && Array.isArray(response.data.rows)) {
          allUsers = [...allUsers, ...response.data.rows];
          totalPages = response.data.meta?.totalPages || 1;
        } else {
          break;
        }
        
        currentPage++;
      } while (currentPage <= totalPages);
      
      setUsers(allUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  // Calculate Estimated Current Value (matching PHP logic exactly)
  const calculateCurrentValue = (data) => {
    let currentValue = 0.00;
    let calculationLog = ["Asset data insufficient for calculation."];

    if (data?.asset_cost && !isNaN(parseFloat(data.asset_cost)) && data?.asset_date_purchased && data?.state) {
      const pv = parseFloat(data.asset_cost);
      const purchaseDateStr = data.asset_date_purchased;
      const assetState = data.state.toLowerCase().trim();

      let purchaseDateTime = null;
      
      const dateStr = purchaseDateStr.trim();
      purchaseDateTime = new Date(dateStr);
      
      if (isNaN(purchaseDateTime.getTime())) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          purchaseDateTime = new Date(parts[2], parts[1] - 1, parts[0]);
        }
      }

      if (!isNaN(purchaseDateTime.getTime()) && pv > 0) {
        const today = new Date();
        calculationLog = [
          `Purchase Value (PV): R ${pv.toFixed(2)}`,
          `Purchase Date: ${purchaseDateTime.toISOString().split('T')[0]}`,
          `Asset Condition: ${assetState.charAt(0).toUpperCase() + assetState.slice(1)}`
        ];

        if (purchaseDateTime > today) {
          currentValue = pv;
          calculationLog.push("Note: Purchase date is in the future. Value is PV.");
        } else {
          const diffTime = Math.abs(today - purchaseDateTime);
          const diffMonths = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30.44));
          
          calculationLog.push(`Months from Purchase (M): ${diffMonths}`);
          
          const dv = pv / 48;
          calculationLog.push(`Depreciation Value/Month (DV): R ${dv.toFixed(2)} (PV/48)`);
          
          const cv_base = pv - (diffMonths * dv);
          calculationLog.push(`Base Calculated Value (CV_base): R ${cv_base.toFixed(2)} (PV - M*DV)`);

          let intermediate_value = 0;
          let appliedFormulaText = "";

          switch (assetState) {
            case 'new':
              intermediate_value = pv;
              appliedFormulaText = "PV";
              currentValue = intermediate_value;
              break;
            case 'good':
              intermediate_value = cv_base;
              appliedFormulaText = "CV_base (PV - M*DV)";
              currentValue = intermediate_value;
              break;
            case 'fair':
              intermediate_value = cv_base + 600;
              appliedFormulaText = "CV_base + 600";
              currentValue = Math.max(intermediate_value, 600);
              break;
            case 'retired':
              intermediate_value = cv_base + 400;
              appliedFormulaText = "CV_base + 400";
              currentValue = Math.max(intermediate_value, 400);
              break;
            case 'scrapped':
              intermediate_value = cv_base + 50;
              appliedFormulaText = "CV_base + 50";
              currentValue = Math.max(intermediate_value, 50);
              break;
            default:
              calculationLog.push(`Note: Condition '${data.state}' not standard. Using age-based fallback.`);
              if (diffMonths <= 24) {
                intermediate_value = pv;
                appliedFormulaText = "PV (M <= 24)";
              } else {
                intermediate_value = cv_base;
                appliedFormulaText = "CV_base (M > 24)";
              }
              currentValue = intermediate_value;
              break;
          }
          
          calculationLog.push(`Applied Formula: ${appliedFormulaText} = R ${intermediate_value.toFixed(2)}`);

          if ((assetState === 'fair' && intermediate_value < 600 && currentValue === 600) ||
              (assetState === 'retired' && intermediate_value < 400 && currentValue === 400) ||
              (assetState === 'scrapped' && intermediate_value < 50 && currentValue === 50)) {
            calculationLog.push(`Adjustment: Value for '${assetState.charAt(0).toUpperCase() + assetState.slice(1)}' raised to its specific minimum of R ${currentValue.toFixed(2)}.`);
          }

          if (currentValue < 0) {
            calculationLog.push(`Adjustment: Final value floored to R 0.00 (was R ${currentValue.toFixed(2)}).`);
            currentValue = 0;
          }
        }
      } else if (isNaN(purchaseDateTime.getTime())) {
        calculationLog = [`Could not parse purchase date: '${purchaseDateStr}'.`];
      } else if (pv <= 0) {
        calculationLog = ["Asset cost is R0.00 or not positive."];
      }
    }

    return { currentValue, calculationLog };
  };



  const handleDisposeAsset = () => {
    if (data?.asset_id) {
      if (window.confirm('Are you sure you want to dispose this asset? This action cannot be undone.')) {
        // TODO: Implement dispose functionality
        toast.info('Dispose functionality will be implemented soon');
        console.log('Dispose asset:', data.asset_id);
      }
    }
  };

  const handleAllocationInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    setAllocationData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (value || '')
    }));
  };

  const handleAllocateSubmit = async (e) => {
    e.preventDefault();
    
    if (!allocationData.allocate_staff_id && allocationData.allocate_staff_id !== '0') {
      toast.error('Please select a user to allocate the asset to');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Convert boolean checkbox values to string labels for the API
      const submissionData = {
        ...allocationData,
        carry_bag: allocationData.carry_bag ? 'Carry Bag' : '',
        other_hardware: allocationData.other_hardware ? 'Other Hardware' : '',
        windows_version: allocationData.windows_version ? 'Windows' : '',
        office_version: allocationData.office_version ? 'MS Office' : '',
        keyboard: allocationData.keyboard ? 'Keyboard' : '',
        mouse: allocationData.mouse ? 'Mouse' : '',
      };
      
      const result = await allocateAsset(submissionData);
      
      if (result) {
        toast.success('Asset allocated successfully');
        setShowAllocationForm(false);
        fetchAssetData(); // Refresh asset data
        fetchAssetHistory(); // Refresh history
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Error allocating asset:', error);
      toast.error('Failed to allocate asset');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setAssetData(null);
    setAssetHistory([]);
    setShowAllocationForm(false);
    setAllocationData({
      asset_id: '',
      allocate_staff_id: '',
      allocate_type: '',
      carry_bag: false,
      other_hardware: false,
      keyboard: false,
      mouse: false,
      windows_version: false,
      office_version: false,
      declaration: false
    });
    onClose();
  };

  const handleResendDeclarationEmail = async () => {
    setResendingEmail(true);
    
    try {
      const data = assetData || asset;
      const userId = data.user_id || data.allocate_staff_id || data.asset_staff_id;
      
      if (!userId) {
        toast.error('Cannot resend email: User information not found');
        return;
      }

      await sendDeclarationEmail({
        ticket_id: 999999, // Pseudo ticket ID for standalone declarations
        user_id: userId,
        asset_ids: [data.asset_id]
      });
      
      toast.success('Declaration email sent successfully');
    } catch (error) {
      console.error('Error resending declaration email:', error);
      toast.error('Failed to send declaration email');
    } finally {
      setResendingEmail(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: '2-digit'
      });
    } catch {
      return dateString || 'N/A';
    }
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Hardware Asset Details" size="xl">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Modal>
    );
  }

  const data = assetData || asset;
  const { currentValue, calculationLog } = calculateCurrentValue(data);
  const showCalculationInfo = calculationLog.length > 1 || calculationLog[0] !== "Asset data insufficient for calculation.";

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Hardware Asset Details" size="2xl">
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left Column - Asset Details */}
          <div className="space-y-3">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900">Asset Information</h3>
            </div>
            <div className="overflow-hidden border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50 w-1/3">Asset Name/Model</td>
                    <td className="px-3 py-2 text-xs text-gray-900 font-medium">{data?.asset_name}</td>
                  </tr>
                  
                  {data?.vendor && (
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Vendor</td>
                      <td className="px-3 py-2 text-xs text-gray-900">{data.vendor}</td>
                    </tr>
                  )}
                  
                  {data?.category && (
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Category</td>
                      <td className="px-3 py-2 text-xs text-gray-900">{data.category}</td>
                    </tr>
                  )}
                  
                  {data?.state && (
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Condition</td>
                      <td className="px-3 py-2 text-xs text-gray-900">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          data.state.toLowerCase() === 'new' ? 'bg-green-100 text-green-800' :
                          data.state.toLowerCase() === 'good' ? 'bg-blue-100 text-blue-800' :
                          data.state.toLowerCase() === 'fair' ? 'bg-yellow-100 text-yellow-800' :
                          data.state.toLowerCase() === 'retired' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {data.state}
                        </span>
                      </td>
                    </tr>
                  )}
                  
                  {data?.asset_tag && (
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Asset Tag</td>
                      <td className="px-3 py-2 text-xs text-gray-900 font-mono">{data.asset_tag}</td>
                    </tr>
                  )}
                  
                  {data?.asset_serial_number && (
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Serial Number</td>
                      <td className="px-3 py-2 text-xs text-gray-900 font-mono">{data.asset_serial_number}</td>
                    </tr>
                  )}
                  
                  {data?.asset_date_purchased && (
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Purchase Date</td>
                      <td className="px-3 py-2 text-xs text-gray-900">{data.asset_date_purchased}</td>
                    </tr>
                  )}
                  
                  {data?.asset_ownership !== undefined && (
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Ownership</td>
                      <td className="px-3 py-2 text-xs text-gray-900">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          data.asset_ownership === 1 || data.asset_ownership === "1" ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                        }`}>
                          {data.asset_ownership === 1 || data.asset_ownership === "1" ? "Owned" : "Rental"}
                        </span>
                      </td>
                    </tr>
                  )}
                  
                  {data?.asset_cost && (
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Asset Cost</td>
                      <td className="px-3 py-2 text-xs text-gray-900 font-semibold text-green-700">
                        R{parseFloat(data.asset_cost).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  )}
                  
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">
                      <div className="flex items-center">
                        Current Value
                        {showCalculationInfo && (
                          <button
                            type="button"
                            className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                            title={calculationLog.join('\n')}
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-900 font-semibold text-blue-700">
                      R{currentValue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                  
                  {data?.processor && (
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Processor</td>
                      <td className="px-3 py-2 text-xs text-gray-900">{data.processor}</td>
                    </tr>
                  )}
                  
                  {data?.storage && (
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Storage</td>
                      <td className="px-3 py-2 text-xs text-gray-900">{data.storage}</td>
                    </tr>
                  )}
                  
                  {data?.ram && (
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">RAM</td>
                      <td className="px-3 py-2 text-xs text-gray-900">{data.ram}</td>
                    </tr>
                  )}
                  
                  {/* Current User or Site */}
                  {(data?.asset_site === 0 || data?.asset_site === null || data?.asset_site === undefined) ? (
                    <>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Current User</td>
                        <td className="px-3 py-2 text-xs text-gray-900">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            data?.user_full_name || data?.user?.user_full_name || data?.current_user ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {data?.user_full_name || data?.user?.user_full_name || data?.current_user || "Not Allocated"}
                          </span>
                        </td>
                      </tr>
                      {data?.allocate_declare == 1 && !data?.allocate_declared && (
                        <tr className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Declaration Status</td>
                          <td className="px-3 py-2 text-xs text-gray-900">
                            <div className="flex items-center justify-between">
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Awaiting User Declaration
                              </span>
                              {can("Manage Assets") && (
                                <button
                                  type="button"
                                  onClick={handleResendDeclarationEmail}
                                  disabled={resendingEmail}
                                  className="ml-2 inline-flex items-center px-2 py-1 border border-blue-300 rounded text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Resend declaration notification email to user"
                                >
                                  {resendingEmail ? (
                                    <>
                                      <svg className="animate-spin w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      Sending...
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                      </svg>
                                      Resend Email
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ) : (
                    data?.site_name && (
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50">Current Site</td>
                        <td className="px-3 py-2 text-xs text-gray-900">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            {data.site_name}
                          </span>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right Column - Allocation and History */}
          <div className="space-y-3">
            {/* Action Buttons */}
            {can('Manage Assets') && (
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Management</h3>
                <div className="flex space-x-2">
                  {!showAllocationForm && (
                    <button
                      type="button"
                      className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      onClick={() => setShowAllocationForm(true)}
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Allocate
                    </button>
                  )}
                  <button
                    type="button"
                    className="inline-flex items-center px-3 py-1.5 border border-red-300 rounded-md text-xs font-medium text-red-700 bg-white hover:bg-red-50 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    onClick={handleDisposeAsset}
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Dispose
                  </button>
                </div>
              </div>
            )}

            {/* Allocation Form */}
            {showAllocationForm && can('Manage Assets') && (
              <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <div className="flex items-center mb-3">
                  <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h4 className="text-sm font-medium text-gray-900">Asset Allocation</h4>
                </div>
                <form onSubmit={handleAllocateSubmit} className="space-y-3">
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">User</label>
                      <select
                        name="allocate_staff_id"
                        value={allocationData.allocate_staff_id || ''}
                        onChange={handleAllocationInputChange}
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="0">No One</option>
                        {users.map((user) => (
                          <option 
                            key={user.user_id} 
                            value={user.user_id}
                          >
                            {user.user_full_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                      <select
                        name="allocate_type"
                        value={allocationData.allocate_type || ''}
                        onChange={handleAllocationInputChange}
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Type</option>
                        <option value="1">Personal</option>
                        <option value="2">Shared</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center">
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          name="declaration"
                          checked={allocationData.declaration || false}
                          onChange={handleAllocationInputChange}
                          className="mr-1"
                        />
                        Requires Declaration
                      </label>
                    </div>
                  </div>

                  {allocationData.declaration && (
                    <div className="border-t pt-3">
                      <label className="block text-xs font-medium text-gray-700 mb-2">Additional Items</label>
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            name="carry_bag"
                            checked={allocationData.carry_bag || false}
                            onChange={handleAllocationInputChange}
                            className="mr-1"
                          />
                          Carry Bag
                        </label>
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            name="keyboard"
                            checked={allocationData.keyboard || false}
                            onChange={handleAllocationInputChange}
                            className="mr-1"
                          />
                          Keyboard
                        </label>
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            name="mouse"
                            checked={allocationData.mouse || false}
                            onChange={handleAllocationInputChange}
                            className="mr-1"
                          />
                          Mouse
                        </label>
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            name="windows_version"
                            checked={allocationData.windows_version || false}
                            onChange={handleAllocationInputChange}
                            className="mr-1"
                          />
                          Windows
                        </label>
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            name="office_version"
                            checked={allocationData.office_version || false}
                            onChange={handleAllocationInputChange}
                            className="mr-1"
                          />
                          MS Office
                        </label>
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            name="other_hardware"
                            checked={allocationData.other_hardware || false}
                            onChange={handleAllocationInputChange}
                            className="mr-1"
                          />
                          Other Hardware
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end space-x-2 pt-3 border-t border-blue-200">
                    <button
                      type="button"
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                      onClick={() => setShowAllocationForm(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Allocating...' : 'Allocate'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* History Section */}
            <div className="space-y-2">
              <div className="flex items-center">
                <svg className="w-4 h-4 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 className="text-sm font-medium text-gray-900">Asset History</h4>
              </div>
              <div className="overflow-hidden border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Action</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">User</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {assetHistory.length > 0 ? (
                      assetHistory.map((entry, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2 text-xs text-gray-900 font-medium">
                            {formatDate(entry.created_at || entry[4] || entry.date)}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {entry.action || entry[1]}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">
                            {entry.user_full_name || entry[2] || 'System'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="px-3 py-4 text-center">
                          <div className="flex flex-col items-center">
                            <svg className="w-6 h-6 text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="text-xs text-gray-500">No history available</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="button"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            onClick={handleClose}
          >
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ViewHardwareModal;
